import React, { useState, useEffect, Fragment, useCallback, useContext, useMemo } from 'react';
import { Row, Col, Button, Tab, Tabs, Form, OverlayTrigger, Tooltip /*, Spinner*/ } from 'react-bootstrap';
import {
	getProjectFrameworks,
	updateFrameworkValues,
	createProjectFrameworks,
	addMonthEndToFramework,
	addMonthStartToFramework,
	deleteMonthStartToFramework,
	deleteMonthEndToFramework,
	deleteVariant,
	createApplicationEconomicFrameworkValue,
} from '@lib/api';
import UploadDocument from '@components/form/upload/uploadDocuments';
import StatusBar from '@components/StatusBar';
import { InputNumber, Message } from 'primereact';
import { formatToDecimals } from '@lib/helpers/formatting';
import { months } from '@lib/helpers/calendar';
import { IcoPlus, IcoMinusCircled } from '@icons';
import DataHeader from 'components/DataHeader';
import { OETrans } from 'components/translation/OETrans';
import { translate } from 'components/translation/translation';
import { DialogConfirmation } from 'components/atomic/molecules/index';
import BudgetOverview from './BudgetOverview';
import { UserContext } from 'components/contexts/UserProvider';
import { Loading } from '@components/atomic/atoms';

export const SwitchShowMonths = ({
	filteredValues,
	months,
	handleTargetAmountChange,
	companyIndex,
	wpIndex,
	frameworkIndex,
	localLang,
	totalPerMonths,
	isActive,
	isCoordinator,
	isEnabledToView,
	isThisBeneficiary,
}) => {
	const [showMonths, setShowMonths] = useState(false);

	return (
		<div className="py-3 ps-3">
			<Row>
				<Col className="">
					<Row className="my-3">
						<Col className="col-auto me-4 fw-bold">Totale</Col>
						<Col className="text-end">{formatToDecimals(totalPerMonths) + ' €'}</Col>
					</Row>
				</Col>
				<Col id="col-space-planning">&nbsp;</Col>
				<Col className="col-auto ms-auto">
					<Form.Check
						className="my-3"
						inline
						label={'Visualizza mesi'}
						name="group1"
						type="switch"
						value="showMonths"
						defaultChecked={showMonths}
						onClick={(e) => setShowMonths(e.target.checked)}
					/>
				</Col>
			</Row>

			{showMonths === true ? (
				<Row className="overflow-auto">
					{filteredValues
						.sort((valueA, valueB) => valueA.month - valueB.month)
						.map((econValue, valueIndex) => {
							//console.log('econValue', econValue);
							return (
								<Col key={'econValue' + econValue.id} className="pt-3 pb-4 col-month">
									<Row className="flex-row">
										<Form.Label className={`fw-normal ${!isEnabledToView && 'text-end'}`}>{months[econValue.month - 1]}</Form.Label>
										{isEnabledToView || isCoordinator || isThisBeneficiary ? (
											<InputNumber
												name="value"
												onChange={(e) => handleTargetAmountChange(companyIndex, wpIndex, frameworkIndex, econValue.id, 'value', e.value)}
												min={0}
												//minFractionDigits={0}
												//maxFractionDigits={2}
												//suffix={'€'}
												mode="currency"
												currency="EUR"
												locale={localLang}
												className="md"
												value={econValue.value}
												disabled={!isActive}
											/>
										) : (
											<Form.Text as="p" className="text-end">{`${formatToDecimals(econValue.value)} €`}</Form.Text>
										)}
									</Row>
									{/* //only for check */}
									{/* <Row style={{fontSize: '13px'}}>
                              <span>id:{econValue.id}</span>
                              <span>economicFrameworkId:{econValue.projectEconomicFrameworkId}</span><br/>
                              <span>expenseItemId:{econValue.projectExpenseItemId}</span><br/>
                              <span>streamId:{econValue.projectStreamId}</span><br/>
                              <span>year:{econValue.year}</span>
                           </Row> */}
								</Col>
							);
						})}
				</Row>
			) : null}
		</div>
	);
};

const BudgetWp = ({
	thisProject,
	projectNoticeExpenseItems,
	isEnabledToView,
	projectExpenseItems,
	isThereApplicationEconomFramework,
	isCoordinator,
}) => {
	const {
		myUser: { companies },
	} = useContext(UserContext);
	const [projectFrameworks, setProjectFrameworks] = useState(null);
	const [projectExpenseItemsForAmounts, setProjectExpenseItemsForAmounts] = useState([]);
	const [projectFrameworksAmount, setProjectFrameworksAmount] = useState([]);
	const localLang = `${localStorage.getItem('userLanguage').toLowerCase()}-${localStorage.getItem('userLanguage')}`;
	const [isThereWp, setIsThereWp] = useState(false);
	const [activeKeyCompany, setActiveKeyCompany] = useState('');
	const [activeKeyWp, setActiveKeyWp] = useState('');
	const [activeKeyVariant, setActiveKeyVariant] = useState('');
	const [lastVariantId, setLastVariantId] = useState(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [sbShow, setSbShow] = useState(<></>);
	const isThisBeneficiary = useMemo(() => {
		const companyId = activeKeyCompany.split('-')[2];
		//console.log('companyId', companyId);
		const isBeneficiary = companies.some((elCompanyId) => elCompanyId === Number(companyId));
		return isBeneficiary;
	}, [activeKeyCompany, companies]);
	//console.log('isThisBeneficiary', isThisBeneficiary);

	const handleStatusBar = (status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	};

	const handleProjectFrameworks = useCallback(
		(projectFrameworksData) => {
			let tempProjectFrameworks = [];

			for (const [companyCode, wpObjects] of Object.entries(projectFrameworksData)) {
				const wpCodes = Object.keys(wpObjects);
				//console.log('wpCodes', wpCodes);

				let firstWp = projectFrameworksData?.[companyCode]?.[wpCodes?.[0]]?.[0];
				//console.log('firstWp', firstWp);

				tempProjectFrameworks.push({
					//COMPANY INFO
					id: firstWp ? firstWp.budgetContainer?.companyId : '',
					code: companyCode,
					name: firstWp ? firstWp.budgetContainer?.company?.name : '',
					wp: wpCodes
						.sort((a, b) => a.id - b.id)
						.map((wpCode, index) => {
							//WP INFO
							const variants = projectFrameworksData[companyCode][wpCode];
							const firstVariant = projectFrameworksData[companyCode][wpCode][0];

							return {
								id: firstVariant?.budgetContainer.projectStreamId,
								name: firstVariant?.budgetContainer.stream.name,
								wpId: firstVariant?.budgetContainer.stream.streamId ?? '',
								variants: variants
									.sort((a, b) => b.id - a.id)
									.map((variant) => ({
										//VARIANT INFO
										id: variant.id,
										active: variant.active,
										number: variant.number,
										economicFrameworkValues: variant.economicFrameworkValues, //ECONOMICS INFO
									})),
							};
						}),
				});
			}

			tempProjectFrameworks = tempProjectFrameworks.sort((a, b) => a.id - b.id);

			if (tempProjectFrameworks && tempProjectFrameworks.length > 0 && activeKeyCompany !== '' && activeKeyWp !== '') {
				const companyId = activeKeyCompany.split('-')[2];
				const wpId = activeKeyWp.split('-')[2];

				const companyIndex = tempProjectFrameworks.findIndex((company) => company.id === Number(companyId));
				const wpIndex = tempProjectFrameworks[companyIndex]?.wp.findIndex((wp) => wp.id === Number(wpId));

				const tempVariants = tempProjectFrameworks[companyIndex]?.wp[wpIndex]?.variants;
				const tempActiveVariant = tempVariants?.find((item) => item.active === true);
				const tempActiveVariantId = tempActiveVariant ? tempActiveVariant.id : tempVariants[0]?.id;

				setActiveKeyVariant(`variant-${tempActiveVariantId}`);
				setLastVariantId(tempActiveVariantId);
			}

			return tempProjectFrameworks;
		},
		[activeKeyCompany, activeKeyWp],
	);

	const fetchProjectFrameworks = useCallback(async () => {
		getProjectFrameworks(thisProject.id).then((frameworks) => {
			if (frameworks.success) {
				setProjectFrameworks(handleProjectFrameworks(frameworks.data));
			}
		});
	}, [handleProjectFrameworks, thisProject.id]);

	useEffect(() => {
		if (projectFrameworks && projectFrameworks.length > 0 && activeKeyCompany === '' && activeKeyWp === '' && lastVariantId === null) {
			setActiveKeyCompany(`company-tab-${projectFrameworks[0]?.id}`);
			setActiveKeyWp(`wp-tab-${projectFrameworks[0]?.wp?.[0]?.id}`);
			setActiveKeyVariant(`variant-${projectFrameworks[0]?.wp?.[0]?.variants?.[0]?.id}`);
			const activeElement = projectFrameworks[0]?.wp[0]?.variants?.find((item) => item.active === true);
			setLastVariantId(activeElement ? Number(activeElement.id) : null);
		}
	}, [activeKeyCompany, activeKeyWp, lastVariantId, projectFrameworks]);

	const refreshProjectFrameworks = useCallback(() => {
		if (!thisProject.id) return;

		fetchProjectFrameworks();
	}, [fetchProjectFrameworks, thisProject.id]);

	useEffect(() => {
		if (thisProject.id) {
			refreshProjectFrameworks();

			setIsThereWp(thisProject.streams.length > 0);
		}
	}, [refreshProjectFrameworks, thisProject]);

	useEffect(() => {
		setProjectExpenseItemsForAmounts(projectExpenseItems);
	}, [projectExpenseItems]);

	const updateValues = async (projectEconomicFrameworkId) => {
		setSbShow(<></>);

		const arrayForUpdate = projectFrameworksAmount.map((projectFrameworkAmount) => ({
			id: projectFrameworkAmount.id,
			value: projectFrameworkAmount.value,
		}));
		//console.log('arrayForUpdate', arrayForUpdate);

		const result = await updateFrameworkValues(thisProject.id, projectEconomicFrameworkId, arrayForUpdate);
		//console.log('result', result);

		if (result.success) {
			handleStatusBar('success', translate('common.message.edited', 'Elemento modificato con successo'), true);

			setTimeout(() => {
				fetchProjectFrameworks();
			}, 2000);
		} else {
			handleStatusBar(
				'error',
				result.message ?? translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
				false,
			);
		}
	};

	const handleTargetAmountChange = (companyIndex, wpIndex, frameworkIndex, index, type, value) => {
		const updatedTargetsAmount = [...projectFrameworks[companyIndex].wp[wpIndex].variants[frameworkIndex].economicFrameworkValues];

		var array_index = updatedTargetsAmount.findIndex((item) => item.id === index);

		updatedTargetsAmount[array_index][type] = value;

		setProjectFrameworksAmount(updatedTargetsAmount);
	};

	const createNewEconomicFramework = async () => {
		const result = await createApplicationEconomicFrameworkValue(thisProject.id);

		if (result.success) {
			handleStatusBar('success', `Quadro economico generato con successo`, true);

			fetchProjectFrameworks();
		} else {
			handleStatusBar('error', `Non è stato possibile generare il quadro economico`, false);
		}
	};

	const createNewVariant = async () => {
		const companyId = activeKeyCompany.split('-')[2];
		const wpId = activeKeyWp.split('-')[2];

		const result = await createProjectFrameworks({ projectId: thisProject.id, active: 1, companyId: companyId, projectStreamId: wpId });

		if (result.success) {
			handleStatusBar('success', `Variante generata con successo`, true);

			let updatedFrameworks = handleProjectFrameworks(result.data);
			setProjectFrameworks(updatedFrameworks);
		} else {
			handleStatusBar('error', `Non è stato possibile generare la variante`, false);
		}
	};

	const handleSelectedTab = (key, type) => {
		switch (type) {
			case 'company':
				if (key === 'wp-tab-overview') {
					setActiveKeyCompany(key);
					setActiveKeyWp('');
					setActiveKeyVariant('');
					setLastVariantId(null);
				} else {
					const companyId1 = key.split('-')[2];
					const companyIndex1 = projectFrameworks.findIndex((company) => company.id === Number(companyId1));
					const firstWpId = projectFrameworks[companyIndex1]?.wp?.[0]?.id;
					const wpIndex = projectFrameworks[companyIndex1]?.wp.findIndex((wp) => wp.id === Number(firstWpId));
					const activeElement1 = projectFrameworks[companyIndex1]?.wp[wpIndex]?.variants?.find((item) => item.active === true);

					setActiveKeyCompany(key);
					setActiveKeyWp(`wp-tab-${firstWpId}`);
					setActiveKeyVariant(`variant-${activeElement1 ? Number(activeElement1.id) : null}`);
					setLastVariantId(activeElement1 ? Number(activeElement1.id) : null);
				}
				break;
			case 'wp':
				const companyId2 = activeKeyCompany.split('-')[2];
				const companyIndex2 = projectFrameworks.findIndex((company) => company.id === Number(companyId2));
				const wpId = key.split('-')[2];
				const wpIndexSelected = projectFrameworks[companyIndex2]?.wp.findIndex((wp) => wp.id === Number(wpId));
				const activeElement2 = projectFrameworks[companyIndex2]?.wp[wpIndexSelected]?.variants?.find((item) => item.active === true);

				setActiveKeyWp(key);
				setActiveKeyVariant(`variant-${activeElement2 ? Number(activeElement2.id) : null}`);
				setLastVariantId(activeElement2 ? Number(activeElement2.id) : null);
				break;
			case 'variant':
				const variantId = key.split('-')[1];

				if (variantId !== 'document') {
					setActiveKeyVariant(`variant-${Number(variantId)}`);
					setLastVariantId(Number(variantId));
				} else if (variantId === 'document') {
					setActiveKeyVariant(`tab-document-${lastVariantId}`);
				}
				break;
			default:
				break;
		}
	};

	const AmountsTab = () => {
		const handleDeleteVariant = () => {
			setShowDeleteModal(true);
		};

		const removeVariant = async () => {
			const result = await deleteVariant(thisProject.id, lastVariantId);

			if (result.success) {
				setShowDeleteModal(false);

				handleStatusBar('success', `Variante eliminata con successo`, true);

				refreshProjectFrameworks();
			} else {
				handleStatusBar('error', `Non è stato possibile eliminare la variante`, false);
			}
		};

		const addMonth = async (index, frameworkId, type) => {
			let result;

			if (type === 'end') {
				result = await addMonthEndToFramework(thisProject.id, frameworkId);
			} else if (type === 'start') {
				result = await addMonthStartToFramework(thisProject.id, frameworkId);
			}

			if (result.success) {
				let updatedProjectFrameworks = [...projectFrameworks];

				const companyId = activeKeyCompany.split('-')[2];
				const companyIndex = projectFrameworks.findIndex((company) => company.id === Number(companyId));
				const wpId = activeKeyWp.split('-')[2];
				const wpIndex = projectFrameworks[companyIndex]?.wp.findIndex((wp) => wp.id === Number(wpId));

				updatedProjectFrameworks[companyIndex].wp[wpIndex].variants[index].economicFrameworkValues = result.data.economicFrameworkValues;

				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile aggiungere un mese alla variante`, false);
			}
		};

		const deleteMonth = async (index, frameworkId, type) => {
			let result;

			if (type === 'end') {
				result = await deleteMonthEndToFramework(thisProject.id, frameworkId);
			} else if (type === 'start') {
				result = await deleteMonthStartToFramework(thisProject.id, frameworkId);
			}

			if (result.success) {
				let updatedProjectFrameworks = [...projectFrameworks];

				const companyId = activeKeyCompany.split('-')[2];
				const companyIndex = projectFrameworks.findIndex((company) => company.id === Number(companyId));
				const wpId = activeKeyWp.split('-')[2];
				const wpIndex = projectFrameworks[companyIndex]?.wp.findIndex((wp) => wp.id === Number(wpId));

				updatedProjectFrameworks[companyIndex].wp[wpIndex].variants[index].economicFrameworkValues = result.data.economicFrameworkValues;

				setProjectFrameworks(updatedProjectFrameworks);
			} else {
				handleStatusBar('error', `Non è stato possibile eliminare un mese dal quadro economico`, false);
			}
		};

		const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

		return (
			<Fragment>
				<div className="simple-box scenaritab mt-0">
					<Row>
						{!isThereApplicationEconomFramework ? (
							<>
								<Message
									severity="warn"
									text="Non hai ancora creato un quadro economico. Dopo aver creato il quadro economico non sarà possibile modificare streams e sotto voci di spesa"
								/>

								{(isEnabledToView || isCoordinator) && (
									<Row className="mt-5 mb-4">
										<Col className="text-center">
											<ConditionalWrapper
												condition={!isThereWp}
												wrapper={(children) => (
													<OverlayTrigger
														placement="top"
														overlay={<Tooltip>Non sono stati ancora creati dei workplan sul progetto</Tooltip>}
													>
														{children}
													</OverlayTrigger>
												)}
											>
												<span className="d-inline-block" style={{ margin: '4rem auto' }}>
													<Button onClick={createNewEconomicFramework} disabled={!isThereWp}>
														Crea nuovo quadro economico
													</Button>
												</span>
											</ConditionalWrapper>
										</Col>
									</Row>
								)}
							</>
						) : (
							<Tabs
								id="tab-company"
								defaultActiveKey={isCoordinator || isEnabledToView ? `wp-tab-overview` : `company-tab-${projectFrameworks[0]?.id}`}
								activeKey={activeKeyCompany}
								onSelect={(k) => handleSelectedTab(k, 'company')}
								className="mb-5"
							>
								{isCoordinator || isEnabledToView ? (
									<Tab eventKey={`wp-tab-overview`} title="Overview">
										<BudgetOverview projectId={thisProject.id} />
									</Tab>
								) : null}

								{projectFrameworks.map((company, companyIndex) => {
									return (
										<Tab key={`key-tab-company-${company.id}`} eventKey={`company-tab-${company.id}`} title={`${company.name}`}>
											<Tabs
												id={`sub-tab-company-${company.id}`}
												defaultActiveKey={`wp-tab-${company.wp?.[0]?.id}`} //se il default è il primo WP
												activeKey={activeKeyWp}
												onSelect={(k) => handleSelectedTab(k, 'wp')}
												className="mb-5"
											>
												{company.wp.map((wp, wpIndex) => {
													return (
														<Tab key={`key-tab-stream-${wp.id}`} eventKey={`wp-tab-${wp.id}`} title={`${wp.wpId} - ${wp.name}`}>
															<Tabs
																id="controlled-tab-variant"
																defaultActiveKey={`variant-${wp.variants?.[0]?.id}`} //se il default è la prima variante
																activeKey={activeKeyVariant}
																onSelect={(k) => (k === 'add-target' ? createNewVariant() : handleSelectedTab(k, 'variant'))}
																className="mb-3"
															>
																<Tab eventKey={`tab-document-${lastVariantId}`} title="Documenti">
																	{lastVariantId && (
																		<UploadDocument
																			entity="projectEconomicFrameworks"
																			entityId={lastVariantId}
																			key={`key-tab-document-${lastVariantId}`}
																		/>
																	)}
																</Tab>

																{wp.variants.map((variant, variantIndex) => {
																	const totalPerFramework = variant.economicFrameworkValues.reduce((tot, elem) => {
																		return tot + Number(elem.value);
																	}, 0);

																	return (
																		<Tab
																			key={`key-tab-variant-${variant.id}`}
																			eventKey={`variant-${variant.id}`}
																			title={
																				<span>
																					Variante {variant.number}{' '}
																					{variant.active && (isEnabledToView || isCoordinator || isThisBeneficiary) && (
																						<span variant="link" className="ms-1 align-text-top" onClick={() => handleDeleteVariant()}>
																							<IcoMinusCircled className="red-color ico-small plus" />
																						</span>
																					)}
																				</span>
																			}
																		>
																			{(isEnabledToView || isCoordinator || isThisBeneficiary) && (
																				<>
																					<DataHeader
																						showCol1={false}
																						data={[
																							{
																								name: 'Totale per variante',
																								value: (totalPerFramework ? formatToDecimals(totalPerFramework) : 0) + ' €',
																							},
																						]}
																						childrenButton={
																							(isEnabledToView || isCoordinator || isThisBeneficiary) && (
																								<>
																									<Row className="flex-column gap-2">
																										<Col className="pe-0">
																											<Row className="align-items-center">
																												<Col className="me-auto">
																													<OverlayTrigger
																														placement="top"
																														overlay={<Tooltip>Rimuovi/aggiungi mese all'inizio</Tooltip>}
																													>
																														<span>Mese Inizio</span>
																													</OverlayTrigger>
																												</Col>

																												<Col className="col-auto px-0">
																													<Button
																														variant="link"
																														disabled={!variant.active}
																														onClick={() => deleteMonth(variantIndex, variant.id, 'start')}
																													>
																														<IcoMinusCircled className="red-color ico-small plus" />
																													</Button>
																												</Col>
																												<Col className="col-auto ps-1">
																													<Button
																														variant="link"
																														disabled={!variant.active}
																														onClick={() => addMonth(variantIndex, variant.id, 'start')}
																													>
																														<IcoPlus className="second-color ico-small plus" />
																													</Button>
																												</Col>
																											</Row>
																										</Col>

																										<Col className="pe-0">
																											<Row className="align-items-center">
																												<Col className="me-auto">
																													<OverlayTrigger
																														placement="top"
																														overlay={<Tooltip>Rimuovi/aggiungi mese alla fine</Tooltip>}
																													>
																														<span>Mese Fine</span>
																													</OverlayTrigger>
																												</Col>
																												<Col className="col-auto px-0">
																													<Button
																														variant="link"
																														disabled={!variant.active}
																														onClick={() => deleteMonth(variantIndex, variant.id, 'end')}
																													>
																														<IcoMinusCircled className="red-color ico-small plus" />
																													</Button>
																												</Col>
																												<Col className="col-auto ps-1">
																													<Button
																														variant="link"
																														disabled={!variant.active}
																														onClick={() => addMonth(variantIndex, variant.id, 'end')}
																													>
																														<IcoPlus className="second-color ico-small plus" />
																													</Button>
																												</Col>
																											</Row>
																										</Col>
																									</Row>
																								</>
																							)
																						}
																					/>
																				</>
																			)}

																			{projectNoticeExpenseItems.map((expense, index) => {
																				return (
																					<Fragment key={index}>
																						<Row>
																							<Col className="col-2 fw-bold py-3">{expense.name}</Col>
																							<Col className="p-0">
																								{projectExpenseItemsForAmounts
																									.sort((a, b) => a.position - b.position)
																									.map((projectExpense, valueIndex) => {
																										const arrayOfYears = variant.economicFrameworkValues
																											.filter((econFram) => econFram.projectExpenseItemId === projectExpense.id)
																											.filter(
																												(value, index, array) => array.findIndex((v) => v.year === value.year) === index,
																											)
																											.map((el) => el.year);
																										const totalPerExpenseItem = variant.economicFrameworkValues
																											.filter(
																												(econFram) =>
																													econFram.projectExpenseItemId === projectExpense.id &&
																													econFram.projectStreamId === wp.id,
																											)
																											.reduce((tot, elem) => {
																												return tot + Number(elem.value);
																											}, 0);
																										//console.log('projectExpense', projectExpense);
																										//console.log('framework.economicFrameworkValues', framework.economicFrameworkValues);

																										return projectExpense.projectNoticeExpenseItemId === expense.id ? (
																											<Row
																												key={projectExpense.id}
																												style={{
																													borderTop: valueIndex > 0 ? '1px solid rgb(222, 226, 230)' : 'none',
																												}}
																											>
																												<Col className="col-3 px-3 pt-4 align-top">{projectExpense.name}</Col>
																												<Col className="px-3 pt-3">
																													<Row>
																														<Col>
																															<Tabs>
																																{arrayOfYears
																																	.sort((a, b) => a - b)
																																	.map((year) => {
																																		const filteredValues = variant.economicFrameworkValues.filter(
																																			(econFrameValue) =>
																																				econFrameValue.year === year &&
																																				econFrameValue.projectExpenseItemId === projectExpense.id &&
																																				econFrameValue.projectStreamId === wp.id &&
																																				econFrameValue.projectEconomicFrameworkId === variant.id,
																																		);
																																		const totalPerMonths = filteredValues.reduce((tot, elem) => {
																																			return tot + Number(elem.value);
																																		}, 0);
																																		//let showMonths = false;
																																		//console.log('showMonths', showMonths);

																																		return (
																																			<Tab
																																				key={projectExpense.id + ' ' + year}
																																				eventKey={projectExpense.id + ' ' + year}
																																				title={year}
																																			>
																																				<SwitchShowMonths
																																					filteredValues={filteredValues}
																																					months={months()}
																																					handleTargetAmountChange={handleTargetAmountChange}
																																					companyIndex={companyIndex}
																																					wpIndex={wpIndex}
																																					frameworkIndex={variantIndex}
																																					localLang={localLang}
																																					totalPerMonths={totalPerMonths}
																																					isActive={variant.active}
																																					isEnabledToView={isEnabledToView}
																																					isCoordinator={isCoordinator}
																																					companyId={company.id}
																																					isThisBeneficiary={isThisBeneficiary}
																																				/>
																																			</Tab>
																																		);
																																	})}

																																<Tab
																																	eventKey={projectExpense.id + '-total'}
																																	title={'Totale'}
																																	className="py-4 px-3"
																																	style={{ minWidth: '25rem' }}
																																>
																																	<table>
																																		<tbody>
																																			{arrayOfYears.map((year) => {
																																				const totalPerYear = variant.economicFrameworkValues
																																					.filter(
																																						(econFram) =>
																																							econFram.projectExpenseItemId === projectExpense.id &&
																																							econFram.year === year &&
																																							econFram.projectStreamId === wp.id,
																																					)
																																					.reduce((tot, elem) => {
																																						return tot + Number(elem.value);
																																					}, 0);

																																				return (
																																					<tr key={year + '-' + projectExpense.id}>
																																						<td className="py-2 fw-bold">{year}</td>
																																						<td className="py-2 ps-5 text-end">
																																							{formatToDecimals(totalPerYear) + ' €'}
																																						</td>
																																					</tr>
																																				);
																																			})}

																																			<tr>
																																				<td className="py-2 fw-bold text-uppercase">TOTALE</td>
																																				<td className="py-2 ps-5 text-end">
																																					{formatToDecimals(totalPerExpenseItem) + ' €'}
																																				</td>
																																			</tr>
																																		</tbody>
																																	</table>
																																</Tab>
																															</Tabs>
																														</Col>
																													</Row>
																												</Col>
																											</Row>
																										) : (
																											''
																										);
																									})}
																							</Col>
																						</Row>
																					</Fragment>
																				);
																			})}
																			{(isEnabledToView || isCoordinator || isThisBeneficiary) && (
																				<Row className="mt-5 text-center">
																					<Col>
																						<Button
																							onClick={() => updateValues(variant.id)}
																							disabled={projectFrameworksAmount.length === 0 ? true : false}
																						>
																							<OETrans code="common.save" fallback={'Salva'} />
																						</Button>
																					</Col>
																				</Row>
																			)}
																		</Tab>
																	);
																})}

																{(isEnabledToView || isCoordinator) && (
																	<Tab
																		eventKey={'add-target'}
																		title={
																			<OverlayTrigger placement="top" overlay={<Tooltip>Aggiungi quadro economico</Tooltip>}>
																				<IcoPlus className="second-color ico-small plus" />
																			</OverlayTrigger>
																		}
																	></Tab>
																)}
															</Tabs>
														</Tab>
													);
												})}
											</Tabs>
										</Tab>
									);
								})}
							</Tabs>
						)}
					</Row>

					<DialogConfirmation
						title={translate('common.planning.deletevariant', 'Elimina variante')}
						content={translate('common.warning.delete', 'Sei sicuro di voler eliminare questo elemento?')}
						confirmLabel={translate('common.delete', 'Elimina')}
						cancelLabel={translate('common.cancel', 'Annulla')}
						onConfirm={() => removeVariant()}
						onCancel={() => setShowDeleteModal(false)}
						show={showDeleteModal}
						onHide={() => setShowDeleteModal(false)}
					/>
				</div>
			</Fragment>
		);
	};

	return (
		<>
			{!projectFrameworks ? <Loading fixedHeight="50vh" /> : <AmountsTab />}

			{sbShow}
		</>
	);
};
export default BudgetWp;
